import { format } from 'date-fns/format';
import type { FormatDistanceOptions } from 'date-fns/formatDistance';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { formatDistanceToNowStrict } from 'date-fns/formatDistanceToNowStrict';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import type { DateRange, TimeRange } from '../components/DatePicker/types';

type DateType = Date | string | number;

export const DateFormats = {
  dmyhms: 'dd MMM yyyy, H:mm:ss',
  dmhms: 'dd MMM, H:mm:ss',
  dmyhma: 'dd MMM yyyy, H:mm a',
  dmyhm: 'dd MMM yyyy, H:mm',
  hhmm: 'HH:mm',
  mdyhma: 'MMM dd, yyyy h:mm a',
  mdhma: 'MMM dd, h:mma',
  mdyhm: 'MM/dd/yyyy H:mm',
  Mdy: 'MMMM dd, yyyy',
  dMy: 'd MMM yyyy',
  yyyyMMdd: 'yyyy-MM-dd',
  ddMMyyyy: 'dd-MM-yyyy',
  ddMMyyyyhma: 'dd-MM-yyyy h:mm a',
  ddMMM: 'dd MMM',
  dMMM: 'd MMM',
  iso: "yyyy-MM-dd'T'HH:mm:ssxxx",
  hma: 'h:mm a',
  DayMMddyyyy: 'EEEE, MM-dd-yyyy',
  'relative-time-to-now': formatDistanceToNow,
  // "8 hours ago" / "1 day ago" / "2 months ago" — strict (no "about" prefix) + the "ago"
  // suffix. `Number(date)` coerces a stringified-epoch (the shape aggregation returns) since
  // formatDistanceToNowStrict only accepts a Date | numeric timestamp.
  'relative-time-ago': (date: DateType, options?: FormatDistanceOptions) =>
    formatDistanceToNowStrict(Number(date), { addSuffix: true, ...options }),
} as const;

/*
 * WARNING: This function is exposed in the no-code platform utilities.
 * Any API changes must maintain backward compatibility.
 * Documentation updates must be made in packages/carbon/docs/website/utilities.md
 * before deploying breaking changes.
 */
export function formatDate(date: DateType, formatStr: string): string {
  if (!date) {
    return '';
  }
  try {
    const numericDate = Number(date);
    const isStringifiedEpoch = !Number.isNaN(numericDate);
    return isStringifiedEpoch ? format(numericDate, formatStr) : format(date, formatStr);
  } catch (e) {
    console.debug(e);
    if (typeof date === 'number') {
      return String(date);
    } else if (typeof date === 'string') {
      return date;
    }

    // now we know the instance is not a simple string or number, but an object type, it can be a valid Date object, or Invalid Date object, so we need to return empty string because the return type of formatDate mandates it to be a string
    return '';
  }
}

/*
 * WARNING: This function is exposed in the no-code platform utilities.
 * Any API changes must maintain backward compatibility.
 * Documentation updates must be made in packages/carbon/docs/website/utilities.md
 * before deploying breaking changes.
 */
export function formatDateByFormatType(
  date: DateType,
  formatType: string | undefined,
  options?: FormatDistanceOptions,
) {
  // default case
  if (!formatType) return formatDate(date, DateFormats.mdyhm);

  // if we have defined mappings
  if (formatType in DateFormats) {
    const formatter = DateFormats[formatType as keyof typeof DateFormats];

    if (typeof formatter === 'function') {
      return formatter(date, options);
    }
    return formatDate(date, formatter);
  }

  // send to date-fns if it can handle it
  return formatDate(date, formatType);
}

export function getDateByFormatType(date: Date | undefined, formatType: string | undefined) {
  switch (formatType) {
    case 'iso8601':
      return date?.toISOString();
    case 'unix':
      return date?.getTime();
    case undefined:
    default:
      return date?.getTime();
  }
}

export function convertISOStringToDateRange(timeRange?: TimeRange | null): DateRange | undefined {
  if (!timeRange?.since && !timeRange?.until) return;

  return {
    since: timeRange.since ? new Date(timeRange.since) : undefined,
    until: timeRange.until ? new Date(timeRange.until) : undefined,
  };
}

export function convertDateRangeToISOString(dateRange?: DateRange | null): TimeRange | undefined {
  if (!dateRange?.since && !dateRange?.until) return;

  return {
    since: dateRange.since ? dateRange.since.toISOString() : undefined,
    until: dateRange.until ? dateRange.until.toISOString() : undefined,
  };
}

export const getTimezone = () => {
  if (typeof Intl === 'undefined') {
    return 'UTC';
  }
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return tz;
};

/**
 * Converts a UTC date string to a formatted date string in the specified time zone.
 * @param utcDateString - The UTC date string (e.g., "2024-08-24T18:00:00Z")
 * @param timeZone - The IANA time zone identifier
 * @returns A formatted date string in the local time zone.
 */
export function utcDateStringToLocal(utcDateString: string): string {
  const utcDate = new Date(utcDateString);
  const timeZone = getTimezone();
  return formatInTimeZone(utcDate, timeZone, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone });
}

/**
 * Convert a local date to UTC date
 * @param date - local date as a date object
 * @returns new instance of a date obj in UTC timezone
 */
export function localDateToUTCDate(date: Date | undefined = new Date()) {
  return toZonedTime(date, 'UTC');
}

/**
 * Converts a local date string to a formatted date string in UTC.
 * @param localDate - The local date string (e.g., "2024-08-24T18:00:00+05:30")
 * @returns A formatted date string in UTC.
 */
export function localDateToUTCString(localDate: string): string {
  const timeZone = getTimezone();
  return fromZonedTime(localDate, timeZone).toISOString();
}

export function convertUTCTimeRangeToTimezoneRange(timeRange: TimeRange): TimeRange {
  return {
    since: timeRange.since ? utcDateStringToLocal(timeRange.since) : undefined,
    until: timeRange.until ? utcDateStringToLocal(timeRange.until) : undefined,
  };
}

export function convertTimezoneRangeToUTCTimeRange(timeRange: TimeRange): TimeRange {
  return {
    since: timeRange.since ? localDateToUTCString(timeRange.since) : undefined,
    until: timeRange.until ? localDateToUTCString(timeRange.until) : undefined,
  };
}

/**
 * Checks if a date format string corresponds to UTC format.
 *
 * A format is considered UTC if it matches ISO 8601 UTC format pattern:
 * - Contains 'T' as date/time separator (can be quoted)
 * - Contains 'Z' or timezone offset (e.g., +00:00) at the end
 *
 * @example
 * // Valid formats:
 * isUTCFormat('yyyy-MM-dd"T"HH:mm:ss\'Z\'')      // true
 * isUTCFormat('yyyy-MM-dd\'T\'HH:mm:ss+00:00')   // true
 * isUTCFormat('yyyy-MM-dd"T"HH:mm:ss.fff\'Z\'')  // true
 * isUTCFormat('yyyy-MM-ddTHH:mm:ssZ')            // true
 * isUTCFormat('yyyy-MM-ddTHH:mm:ss-05:00')       // true
 *
 * @example
 * // Invalid formats:
 * isUTCFormat('yyyy-MM-dd HH:mm:ss')             // false
 * isUTCFormat('yyyy-MM-ddTHH:mm:ss')             // false
 * isUTCFormat('yyyy-MM-ddZTHH:mm:ss')            // false
 * isUTCFormat('yyyy-MM-dd')                      // false
 * isUTCFormat(undefined)                         // false
 *
 * @param dateFormat - The date format string to check
 * @returns boolean indicating if the format is UTC
 */
export function isUTCFormat(dateFormat: string | undefined): boolean {
  if (!dateFormat) return false;
  return /.*['"]?t['"]?.*(?:['"]?z['"]?|[+-]\d{2}:?\d{2})$/i.test(dateFormat);
}
